<script lang="ts">
import { defineComponent, inject, onActivated, onDeactivated, PropType, ref, watch } from "vue";
import Steps from "@/components/Steps.vue";
import { NavvItem } from "@/model/NavItem";
import Antragsteller from "@/components/person/Antragsteller.vue";
import Zahlungsdaten from "@/components/zahlungsdaten/Zahlungsdaten.vue";
import HugMitgliedschaft from "@/components/hug/HugMitgliedschaft.vue";
import formValidierung from "@/businesslogic/formValidierung";
import Logger from "js-logger";
import formatUtils from "@/businesslogic/utils/formatUtils";
import Zeile from "@/components/grid/Zeile.vue";
import Zelle from "@/components/grid/Zelle.vue";
import Modal from "@/components/Modal.vue";
import DateiUpload from "@/views/vgv/views/DateiUpload.vue";
import Ueberschrift from "@/components/grid/Ueberschrift.vue";
import TitelZelle from "@/components/grid/TitelZelle.vue";
import storeUtils from "@/businesslogic/utils/storeUtils";
import dateUtils from "@/businesslogic/utils/dateUtils";
import VertragsunterlagenDigital from "@/components/abschluss/VertragsunterlagenDigital.vue";
import InfoBox from "@/components/grid/InfoBox.vue";
import { useFormStore } from "@/businesslogic/stores/formStore";

export default defineComponent({
  name: "IhreDatenFH2024",
  components: {
    InfoBox,
    VertragsunterlagenDigital,
    TitelZelle,
    Ueberschrift,
    DateiUpload,
    Steps,
    Antragsteller,
    Zahlungsdaten,
    HugMitgliedschaft,
    Zeile,
    Zelle,
    Modal,
  },
  props: {
    isMakler: Boolean,
    navItems: Object as PropType<NavvItem[]>,
    anfrageId: String,
  },
  emits: ["prev-page", "next-page", "goto-page", "handle-error"],
  setup: function (_props, { emit }) {
    const piniaStore = useFormStore();
    const istSichtbar: any = inject("istSichtbar");
    const wechsleSichtbarkeit: any = inject("wechsleSichtbarkeit");
    const fehler = ref({} as any);
    const zeigeFrageHugNachlass = ref(false);
    const { formatCurrency } = formatUtils();
    const { enthaeltVGV, istFerienwohnung, istNurVHV } = storeUtils();
    const { dateStringToIsoDate } = dateUtils();
    let komponenteIstAktiv = false;
    let beitragsaenderungDurchHugMitgliedschaft = false;
    let beitragsaenderungDurch60PlusTarif = false;
    const modalBeitragsaenderung = ref({
      titel: "Beitragsänderung",
      text: "Durch Ihre Änderung wurde der Beitrag angepasst. Bitte überprüfen Sie den neuen Beitrag.",
      hatSchliessenButton: true,
      schliessenButtonText: "OK",
    });

    const antragstellerRef = ref<InstanceType<typeof Antragsteller>>();
    const zahlungsdatenRef = ref<InstanceType<typeof Zahlungsdaten>>();

    const eingabenSindValide = async () => {
      const { validiereForm, getFormFehlerAnzahl, getFormFehler, validiereZusammengesetzteNamenLaenge } =
        formValidierung();

      await validiereForm();
      validiereZusammengesetzteNamenLaenge();

      let anstragstellerValide: boolean | undefined = true;
      if (getFormFehlerAnzahl() === 0) {
        anstragstellerValide = await antragstellerRef.value?.pruefen();
      }

      Logger.debug("Anzahl Fehler aus Validierung: " + getFormFehlerAnzahl(), anstragstellerValide, getFormFehler());
      fehler.value = getFormFehler();

      return anstragstellerValide && getFormFehlerAnzahl() === 0;
    };

    const nextPage = () => {
      eingabenSindValide().then((isValid) => {
        if (isValid) {
          emit("next-page", { pageIndex: 3 });
        }
      });
    };

    watch(
      () => piniaStore.state.tarifinformationen.zahlungsweise,
      (neu, alt) => {
        if (komponenteIstAktiv && neu !== alt) {
          Logger.debug("WATCH (IhreDaten>zahlungsweise): ZW hat sich geändert.");
          if (
            Number(piniaStore.state.tarifinformationen.zahlungsweise) > 1 &&
            piniaStore.state.zahlungsdaten.zahlungsart !== "debit"
          ) {
            Logger.debug("WATCH (IhreDaten>zahlungsweise): Auf Lastschrift umstellen...");
            piniaStore.state.zahlungsdaten.zahlungsart = "debit";
          }
        }
      },
    );

    watch(
      () => piniaStore.state.tarifinformationen.hugMitglied,
      () => {
        zeigeFrageHugNachlass.value = piniaStore.state.tarifinformationen.hugMitglied;
        beitragsaenderungDurchHugMitgliedschaft = true;
      },
    );

    watch(
      () => piniaStore.state.tarifinformationen.tariff60Plus,
      (wertNeu, wertAlt) => {
        if (wertNeu !== wertAlt) {
          beitragsaenderungDurch60PlusTarif = true;
        }
      },
    );

    watch(
      () => piniaStore.state.preise.produktLinien[piniaStore.state.preise.produktLinien.length - 1].gemZahlungsweise,
      (beitragNeu, beitragAlt) => {
        if (
          komponenteIstAktiv &&
          beitragAlt > 0 &&
          beitragNeu !== beitragAlt &&
          (beitragsaenderungDurch60PlusTarif || beitragsaenderungDurchHugMitgliedschaft)
        ) {
          Logger.debug("WATCH (IhreDaten>zahlbeitrag): Beitrag hat sich geändert.");
          if (beitragsaenderungDurch60PlusTarif) {
            modalBeitragsaenderung.value.titel = "60 Plus";
            modalBeitragsaenderung.value.text = "Durch die Änderung des Geburtsdatums";
            beitragsaenderungDurch60PlusTarif = false;
          } else if (beitragsaenderungDurchHugMitgliedschaft) {
            modalBeitragsaenderung.value.titel = "Mitgliedschaft Haus & Grund-Verein";
            modalBeitragsaenderung.value.text = "Durch die Änderung der Mitgliedschaft in einem Haus & Grund-Verein";
            beitragsaenderungDurchHugMitgliedschaft = false;
          }
          modalBeitragsaenderung.value.text +=
            " ändert sich Ihr Beitrag von " + formatCurrency(beitragAlt) + " auf " + formatCurrency(beitragNeu);
          Logger.debug(modalBeitragsaenderung.value.text);
          wechsleSichtbarkeit("modalBeitragsaenderung");
        }
      },
    );

    onActivated(() => {
      komponenteIstAktiv = true;
      zeigeFrageHugNachlass.value = piniaStore.state.tarifinformationen.hugMitglied;
    });

    onDeactivated(() => {
      komponenteIstAktiv = false;
    });

    function onGeburtsdatumGeaendert() {
      if (
        istNurVHV(piniaStore.state) &&
        istFerienwohnung(piniaStore.state) &&
        dateStringToIsoDate(piniaStore.state.personendaten.versicherungsnehmer.geburtsdatum) !== undefined
      ) {
        Logger.debug("onGeburtsdatumGeaendert: Geburtsdatum valide - neu berechnen...");
        piniaStore.state.neuberechnen = true;
      }
    }

    return {
      piniaStore,
      wechsleSichtbarkeit,
      fehler,
      istSichtbar,
      zeigeFrageHugNachlass,
      modalBeitragsaenderung,
      nextPage,
      antragstellerRef,
      zahlungsdatenRef,
      enthaeltVGV,
      onGeburtsdatumGeaendert,
    };
  },
});
</script>

<template>
  <steps :active="3" v-bind:items="navItems" @goto-page="$emit('goto-page', $event)" />
  <section>
    <antragsteller :fehler="fehler" ref="antragstellerRef" @geburtsdatumGeaendert="onGeburtsdatumGeaendert" />

    <vertragsunterlagen-digital :fehler="fehler" />

    <zahlungsdaten :fehler="fehler" ref="zahlungsdatenRef" />

    <ueberschrift
      v-if="isMakler && enthaeltVGV(piniaStore.state)"
      :zeige-info="true"
      info-box-id="dateienInfo"
      titel="Dateien hochladen"
    />
    <zeile v-if="isMakler && enthaeltVGV(piniaStore.state)">
      <titel-zelle></titel-zelle>
      <zelle>
        <info-box id="dateienInfo">
          Sie können bis zu 10 Dokumente mit jeweils 10 MB hochladen. <br />
          Zulässige Dateiformate: .jpeg, .jpg, .png, .pdf und .tiff.
        </info-box>
        <datei-upload id="dateien" />
      </zelle>
    </zeile>

    <hug-mitgliedschaft v-if="zeigeFrageHugNachlass" :fehler="fehler" />
    <!-- Absenden -->
    <zeile>
      <zelle :offset="4" :breite="6">
        <button
          class="c-button c-button--full-width u-mar-t-2 u-mar-b-2 mobil-mehrAbstand"
          @click="nextPage()"
          data-cy="submitIhreDaten"
        >
          Weiter
        </button>
      </zelle>
    </zeile>
  </section>
  <Modal
    v-if="istSichtbar('modalBeitragsaenderung')"
    v-bind="modalBeitragsaenderung"
    @closeModal="wechsleSichtbarkeit('modalBeitragsaenderung')"
  />
</template>
